import type { AssetStatus, ContentPiece, ContentStage } from "./types";

export type AgentTaskType =
  | "generate_themes"
  | "generate_ideas"
  | "select_finalists"
  | "draft_briefs"
  | "draft_captions"
  | "revise_brief"
  | "generate_placeholders"
  | "build_client_deck";

export type AgentCostTier = "none" | "low" | "high";

export type AgentModelConfig = {
  lowCostModel: string;
  highJudgmentModel: string;
  lowCostReasoningEffort?: AgentReasoningEffort;
  highJudgmentReasoningEffort?: AgentReasoningEffort;
};

export type AgentReasoningEffort = "none" | "low" | "medium" | "high" | "xhigh" | "max";

export type BriefStatus = "draft" | "ready" | "approved";

export type CreativeBriefSnapshot = {
  contentPieceId: string;
  status: BriefStatus;
  body: string;
  revision: number;
};

export type CreativeAssetRole = "placeholder" | "draft" | "revision" | "final";

export type CreativeAssetSnapshot = {
  contentPieceId: string;
  r2Key: string;
  role: CreativeAssetRole;
  status: AssetStatus;
};

export type ClientDeckEligibility = {
  eligible: boolean;
  reasons: string[];
};

const highJudgmentAgentTasks = new Set<AgentTaskType>(["select_finalists"]);

export const defaultAgentModelConfig: AgentModelConfig = {
  lowCostModel: "gpt-5.6-luna",
  highJudgmentModel: "gpt-5.6-terra",
  lowCostReasoningEffort: "medium",
  highJudgmentReasoningEffort: "medium",
};

export function getAgentTaskCostTier(taskType: AgentTaskType): AgentCostTier {
  if (highJudgmentAgentTasks.has(taskType)) {
    return "high";
  }

  return "low";
}

export function getAgentTaskModel(
  taskType: AgentTaskType,
  config: AgentModelConfig = defaultAgentModelConfig,
): string {
  return getAgentTaskCostTier(taskType) === "high" ? config.highJudgmentModel : config.lowCostModel;
}

export function getAgentTaskReasoningEffort(
  taskType: AgentTaskType,
  config: AgentModelConfig = defaultAgentModelConfig,
): AgentReasoningEffort {
  const effort =
    getAgentTaskCostTier(taskType) === "high"
      ? config.highJudgmentReasoningEffort
      : config.lowCostReasoningEffort;

  return effort ?? "medium";
}

export function isAcceptedBrief(
  piece: Pick<ContentPiece, "id" | "stage"> & {
    briefStatus?: string;
    assignmentStatus?: string;
    assignedDesignerId?: string;
    designer?: string;
    briefStale?: boolean;
    captionStale?: boolean;
    clientCaption?: string;
    captionRevision?: number;
    publishingDestinations?: readonly string[];
    hasUnsavedBriefingCandidate?: boolean;
    currentBriefRevision?: number;
    currentCaptionRevision?: number;
    assignmentNeedsReconfirmation?: boolean;
  },
  brief: CreativeBriefSnapshot | undefined,
): boolean {
  if (brief?.status === "approved") {
    return true;
  }
  return Boolean(
    brief?.status === "ready" &&
      piece.briefStatus === "ready" &&
      piece.assignmentStatus === "assigned" &&
      Boolean(piece.assignedDesignerId || piece.designer) &&
      !piece.briefStale &&
      !piece.captionStale &&
      Boolean(piece.clientCaption?.trim()) &&
      Boolean(piece.publishingDestinations?.length) &&
      !piece.hasUnsavedBriefingCandidate &&
      (piece.currentBriefRevision === undefined || brief.revision === piece.currentBriefRevision) &&
      (piece.currentCaptionRevision === undefined || piece.captionRevision === piece.currentCaptionRevision) &&
      !piece.assignmentNeedsReconfirmation,
  );
}

export function hasApprovedDisplayAsset(assets: CreativeAssetSnapshot[]): boolean {
  return assets.some((asset) => asset.status === "approved" && isDisplayAssetRole(asset.role));
}

export function getClientDeckEligibility({
  piece,
  brief,
  assets,
}: {
  piece: Pick<ContentPiece, "id" | "stage"> & Parameters<typeof isAcceptedBrief>[0];
  brief?: CreativeBriefSnapshot;
  assets: CreativeAssetSnapshot[];
}): ClientDeckEligibility {
  const reasons: string[] = [];
  const matchingAssets = assets.filter((asset) => asset.contentPieceId === piece.id);
  const matchingBrief = brief?.contentPieceId === piece.id ? brief : undefined;

  if (!isAcceptedBrief(piece, matchingBrief)) {
    reasons.push("Briefing package must be accepted.");
  }

  if (!hasApprovedDisplayAsset(matchingAssets)) {
    reasons.push("Display asset must be approved internally.");
  }

  return {
    eligible: reasons.length === 0,
    reasons,
  };
}

export function stageAfterBriefApproval(stage: ContentStage): ContentStage {
  if (stage === "brief_draft" || stage === "brief_ready") {
    return "brief_approved";
  }

  return stage;
}

export function stageAfterDesignerAssignment(stage: ContentStage): ContentStage {
  if (stage === "brief_approved") {
    return "assigned_to_designer";
  }

  return stage;
}

export function stageAfterAssetReview(stage: ContentStage, status: AssetStatus): ContentStage {
  if (status === "in_review") {
    return "asset_in_review";
  }

  if (status === "approved") {
    return "asset_approved";
  }

  if (stage === "assigned_to_designer" || stage === "asset_in_review") {
    return "asset_in_progress";
  }

  return stage;
}

function isDisplayAssetRole(role: CreativeAssetRole): boolean {
  return role === "final" || role === "placeholder";
}
